import React, { ReactNode } from "react";
import cn from "classnames";
import { Popover } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";

import styles from "./TableFilters.module.scss";

/** Row of filter controls; lays out search, chips and actions with the table spacing. */
export const FilterBar = ({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) => <div className={cn(styles.bar, className)}>{children}</div>;

type FilterSearchInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
> & {
  value: string;
  onValueChange: (value: string) => void;
  containerClassName?: string;
};

/** Toolbar search box: icon on the left, clear button once there is text. */
export const FilterSearchInput = ({
  value,
  onValueChange,
  placeholder = "Search",
  containerClassName,
  className,
  "aria-label": ariaLabel,
  ...props
}: FilterSearchInputProps) => (
  <div className={cn(styles.search, containerClassName)}>
    <SearchIcon aria-hidden className={styles.searchIcon} />
    <input
      {...props}
      type="search"
      aria-label={ariaLabel ?? placeholder}
      placeholder={placeholder}
      className={cn(styles.searchInput, className)}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
    />
    {value && (
      <button
        type="button"
        aria-label="Clear search"
        className={cn(styles.iconButton, styles.searchClear)}
        onClick={() => onValueChange("")}
      >
        <CloseIcon aria-hidden />
      </button>
    )}
  </div>
);

type FilterTriggerProps = {
  label: ReactNode;
  /** Shown after a divider while the filter is active, e.g. the chosen name or "3 selected". */
  value?: ReactNode;
  active?: boolean;
  open?: boolean;
  disabled?: boolean;
  title?: string;
  icon?: ReactNode;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Adds a clear button while active. */
  onClear?: () => void;
  clearLabel?: string;
  className?: string;
};

/** Faceted filter chip: dashed while empty, solid with the chosen value once applied. */
export const FilterTrigger = React.forwardRef<HTMLButtonElement, FilterTriggerProps>(
  (
    {
      label,
      value,
      active = false,
      open = false,
      disabled = false,
      title,
      icon,
      onClick,
      onClear,
      clearLabel,
      className,
    },
    ref,
  ) => {
    const showClear = active && !!onClear && !disabled;
    return (
      <span
        className={cn(
          styles.trigger,
          active && styles.trigger_active,
          open && styles.trigger_open,
          disabled && styles.trigger_disabled,
          className,
        )}
        title={title}
      >
        <button
          ref={ref}
          type="button"
          className={styles.triggerButton}
          aria-haspopup="dialog"
          aria-expanded={open}
          disabled={disabled}
          onClick={onClick}
        >
          {icon ?? <AddCircleOutlineIcon aria-hidden className={styles.triggerIcon} />}
          <span className={styles.triggerLabel}>{label}</span>
          {active && value !== undefined && value !== null && value !== "" && (
            <>
              <span className={styles.triggerDivider} aria-hidden />
              <span className={styles.triggerValue}>{value}</span>
            </>
          )}
          {!showClear && <ExpandMoreIcon aria-hidden className={styles.chevron} />}
        </button>
        {showClear && (
          <button
            type="button"
            className={cn(styles.iconButton, styles.triggerClear)}
            aria-label={clearLabel ?? (typeof label === "string" ? `Clear ${label}` : "Clear filter")}
            onClick={onClear}
          >
            <CloseIcon aria-hidden />
          </button>
        )}
      </span>
    );
  },
);
FilterTrigger.displayName = "FilterTrigger";

type FilterPopoverProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Accessible name of the panel, e.g. "Filter Vehicle". */
  label: string;
  children: ReactNode;
  className?: string;
};

/** Dropdown panel under a trigger; portalled so table overflow never clips it. */
export const FilterPopover = ({
  anchorEl,
  onClose,
  label,
  children,
  className,
}: FilterPopoverProps) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    transformOrigin={{ vertical: "top", horizontal: "left" }}
    PaperProps={{ className: cn(styles.panel, className), elevation: 0 }}
  >
    <div role="dialog" aria-label={label}>
      {children}
    </div>
  </Popover>
);

/** Search box at the top of a panel. */
export const FilterPanelSearch = ({
  value,
  onValueChange,
  placeholder = "Search",
  "aria-label": ariaLabel,
}: {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  "aria-label"?: string;
}) => (
  <div className={styles.panelSearch}>
    <SearchIcon aria-hidden className={styles.searchIcon} />
    <input
      autoFocus
      type="search"
      aria-label={ariaLabel ?? placeholder}
      placeholder={placeholder}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
    />
  </div>
);

export const FilterOptionList = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} {...props} className={cn(styles.list, className)} />
));
FilterOptionList.displayName = "FilterOptionList";

type FilterOptionProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  selected: boolean;
  /** Checkbox for multi-select, check mark for single-select. */
  multiple?: boolean;
  icon?: ReactNode;
  children: ReactNode;
};

export const FilterOption = ({
  selected,
  multiple = false,
  icon,
  children,
  className,
  ...props
}: FilterOptionProps) => (
  <button
    type="button"
    aria-pressed={selected}
    {...props}
    className={cn(styles.option, selected && styles.option_selected, className)}
  >
    <span
      aria-hidden
      className={cn(
        styles.check,
        multiple ? styles.check_box : styles.check_radio,
        selected && styles.check_on,
      )}
    >
      {selected && <CheckIcon />}
    </span>
    {icon && <span className={styles.optionIcon}>{icon}</span>}
    <span className={styles.optionLabel} title={typeof children === "string" ? children : undefined}>
      {children}
    </span>
  </button>
);

export const FilterPanelMessage = ({
  children,
  role,
}: {
  children: ReactNode;
  role?: "status" | "alert";
}) => (
  <div className={styles.message} role={role}>
    {children}
  </div>
);

export const FilterPanelHint = ({ children }: { children: ReactNode }) => (
  <p className={styles.hint}>{children}</p>
);

/** Clear on the left; Cancel and Apply on the right. */
export const FilterPanelFooter = ({
  start,
  children,
}: {
  start?: ReactNode;
  children: ReactNode;
}) => (
  <div className={styles.footer}>
    {start && <span className={styles.footerStart}>{start}</span>}
    {children}
  </div>
);

type SegmentedOption<V extends string> = { value: V; label: string; icon?: ReactNode };

/** Two or three way switch in the toolbar, e.g. list or cards. */
export const FilterSegmented = <V extends string>({
  options,
  value,
  onChange,
  "aria-label": ariaLabel,
  className,
}: {
  options: SegmentedOption<V>[];
  value: V;
  onChange: (value: V) => void;
  "aria-label": string;
  className?: string;
}) => (
  <div role="group" aria-label={ariaLabel} className={cn(styles.segmented, className)}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        aria-pressed={option.value === value}
        className={cn(styles.segment, option.value === value && styles.segment_active)}
        onClick={() => onChange(option.value)}
      >
        {option.icon}
        {option.label}
      </button>
    ))}
  </div>
);

export const filterStyles = styles;
